import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { track } from "../lib/analytics";
import { OcrTrainer } from "../components/OcrTrainer";
import { LoyaltyConfigEditor } from "../components/LoyaltyConfigEditor";
import { APP_URL } from "../lib/config";

export function Welcome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const tenantId = params.get("tenant");
  const [tenantName, setTenantName] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      nav("/", { replace: true });
      return;
    }
    track("welcome_viewed", { tenant_id: tenantId });
    (async () => {
      try {
        const snap = await getDoc(doc(db, "tenants", tenantId));
        if (snap.exists()) setTenantName(snap.data().name);
      } catch {
        /* ignore */
      }
    })();
  }, [tenantId, nav]);

  if (!tenantId) return null;

  const joinUrl = `${APP_URL}/?join=${encodeURIComponent(tenantId)}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=20&data=${encodeURIComponent(
    joinUrl
  )}`;

  const copyLink = async () => {
    track("qr_link_copied", { tenant_id: tenantId });
    try {
      await navigator.clipboard.writeText(joinUrl);
    } catch {
      /* ignore */
    }
  };

  const share = async () => {
    track("qr_shared", { tenant_id: tenantId });
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${tenantName ?? "Tu comercio"} en Fideliza`,
          text: "Suma puntos y canjea rewards en cada compra.",
          url: joinUrl
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col gap-6">
        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Fideliza</p>
          <h1 className="mt-2 font-display text-3xl font-semibold text-leaf-950">
            ¡Bienvenido{user?.displayName ? `, ${user.displayName.split(" ")[0]}` : ""}!
          </h1>
          {tenantName && (
            <p className="mt-2 text-base text-leaf-700">
              <span className="font-semibold text-leaf-900">{tenantName}</span> ya está activo en
              Fideliza.
            </p>
          )}
        </header>

        <section className="card flex flex-col items-center gap-4">
          <p className="text-xs uppercase tracking-wider text-leaf-600">Tu QR de fidelización</p>
          <div className="rounded-2xl bg-white p-4 ring-1 ring-leaf-100">
            <img src={qrUrl} alt="QR de tu local" className="h-64 w-64" />
          </div>
          <p className="text-center text-sm text-leaf-700">
            Imprímelo y ponlo en tu caja, mesas, redes.
            <br />
            Tus clientes escanean → ya están sumando tickets.
          </p>
          <div className="flex w-full flex-col gap-2">
            <a
              href={qrUrl}
              download={`fideliza-${tenantId}-qr.png`}
              onClick={() => track("qr_downloaded", { tenant_id: tenantId })}
              className="btn-primary"
            >
              Descargar QR (PNG)
            </a>
            <button type="button" onClick={share} className="btn-secondary">
              Compartir link
            </button>
            <button type="button" onClick={copyLink} className="btn-ghost text-sm">
              Copiar link directo
            </button>
          </div>
        </section>

        {user && tenantId && (
          <section className="card flex flex-col gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-leaf-600">Paso 1 · OCR</p>
              <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                Sube una boleta de tu negocio
              </h2>
              <p className="mt-1 text-sm text-leaf-700">
                Nuestro OCR la lee y detecta tus datos (RUT, productos, precios). Confirmas que es
                lo correcto y a partir de ahí solo se aceptarán boletas tuyas, validadas
                automáticamente. <strong>No se podrá subir la misma boleta dos veces</strong>{" "}
                gracias al folio + monto + comercio.
              </p>
            </div>
            <OcrTrainer tenantId={tenantId} uid={user.uid} />
          </section>
        )}

        {tenantId && (
          <section className="card flex flex-col gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-leaf-600">Paso 2 · Reward</p>
              <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                Define tu programa de loyalty
              </h2>
              <p className="mt-1 text-sm text-leaf-700">
                Decide cuántas compras necesita tu cliente para ganar 1 reward y qué le vas a
                regalar.
              </p>
            </div>
            <LoyaltyConfigEditor tenantId={tenantId} />
          </section>
        )}

        <section className="card flex flex-col gap-3">
          <p className="text-xs uppercase tracking-wider text-leaf-600">Otros</p>
          <StepItem
            num="✓"
            title="Prueba la app como cliente"
            body="Abre el link en tu celular para ver cómo se ve. Es exactamente lo que verán tus clientes al escanear."
            cta={{ label: "Abrir como cliente", href: joinUrl, external: true }}
          />
          <StepItem
            num="i"
            title="Verificación de tu cuenta"
            body="Tu cuenta está activa. Vamos a verificar tu RUT en SII dentro de las próximas 24 h. Te avisamos por email."
          />
        </section>

        <button onClick={() => nav("/")} className="btn-ghost text-sm">
          Ir a mi panel
        </button>
      </div>
    </main>
  );
}

function StepItem(props: {
  num: string;
  title: string;
  body: string;
  cta?: { label: string; href?: string; to?: string; external?: boolean };
  disabled?: boolean;
  disabledHint?: string;
}) {
  return (
    <div className="flex gap-3 rounded-2xl border border-leaf-100 bg-white p-4">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-leaf-100 font-display font-semibold text-leaf-800">
        {props.num}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-leaf-950">{props.title}</p>
        <p className="mt-1 text-sm text-leaf-700">{props.body}</p>
        {props.cta && (
          <div className="mt-3">
            {props.disabled ? (
              <span className="text-xs italic text-leaf-500">{props.disabledHint}</span>
            ) : props.cta.href ? (
              <a
                href={props.cta.href}
                target={props.cta.external ? "_blank" : undefined}
                rel={props.cta.external ? "noreferrer" : undefined}
                className="btn-secondary px-4 py-2 text-sm"
              >
                {props.cta.label}
              </a>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
